/**
 * Category-specific product fields written by the seller apps' product form
 * (Shop_owner_app: lib/nav_screens/ProductPage/model/product_spec.dart).
 * Read-only mirror for the admin panel, which has no product-edit UI at all.
 */

export const FORMULATIONS = {
  EC: 'Emulsifiable concentrate',
  SC: 'Suspension concentrate',
  SL: 'Soluble liquid',
  EW: 'Emulsion, oil in water',
  WP: 'Wettable powder',
  WG: 'Water-dispersible granules',
  SP: 'Soluble powder',
  GR: 'Granules',
  DP: 'Dustable powder',
  ULV: 'Ultra-low volume liquid',
};

export const HAZARD_CLASSES = ['Ia', 'Ib', 'II', 'III', 'U'];
export const FERTILIZER_FORMS = ['granular', 'powder', 'liquid', 'crystal'];
export const CONDITIONS = ['new', 'used'];

// `categories: 'all'` = written for every product; otherwise the exact list
// of productCategory values that field applies to (matches product_spec.dart's
// toMap() gating).
export const SPEC_FIELDS = [
  { key: 'packaging', label: 'Packaging', type: 'text', categories: 'all' },
  { key: 'shippingWeightKg', label: 'Shipping Weight (kg)', type: 'number', categories: 'all' },
  { key: 'batchNumber', label: 'Batch Number', type: 'text', categories: ['seeds', 'fertilizers', 'pesticides'] },
  { key: 'expiryDate', label: 'Expiry Date', type: 'date', categories: ['seeds', 'fertilizers', 'pesticides'] },
  { key: 'registrationNumber', label: 'Registration / Certificate No.', type: 'text', categories: ['seeds', 'fertilizers', 'pesticides'] },
  { key: 'formulation', label: 'Formulation', type: 'select', options: FORMULATIONS, categories: ['pesticides'] },
  { key: 'hazardClass', label: 'WHO Hazard Class', type: 'select', options: HAZARD_CLASSES, categories: ['pesticides'] },
  { key: 'targetUse', label: 'Target Use', type: 'text', categories: ['pesticides'] },
  { key: 'preHarvestDays', label: 'Pre-Harvest Interval (days)', type: 'number', categories: ['pesticides'] },
  { key: 'npk', label: 'NPK Ratio', type: 'text', categories: ['fertilizers'] },
  { key: 'fertilizerForm', label: 'Form', type: 'select', options: FERTILIZER_FORMS, categories: ['fertilizers'] },
  { key: 'variety', label: 'Variety', type: 'text', categories: ['seeds'] },
  { key: 'maturityDays', label: 'Maturity (days)', type: 'number', categories: ['seeds'] },
  { key: 'germinationRate', label: 'Germination Rate (%)', type: 'number', categories: ['seeds'] },
  { key: 'modelName', label: 'Model', type: 'text', categories: ['machines', 'tools'] },
  { key: 'powerRating', label: 'Power Rating', type: 'text', categories: ['machines'] },
  { key: 'itemCondition', label: 'Condition', type: 'select', options: CONDITIONS, categories: ['machines', 'tools'] },
  { key: 'warrantyMonths', label: 'Warranty (months)', type: 'number', categories: ['machines', 'tools'] },
];

// targetUse is relabeled for fertilizers (mirrors mobile's fieldRecommendedCrops
// vs fieldTargetUse switch in product_spec_list.dart).
export function fieldLabel(field, category) {
  if (field.key === 'targetUse' && (category || '').toLowerCase() === 'fertilizers') {
    return 'Recommended Crops';
  }
  return field.label;
}

export function fieldsForCategory(category) {
  const c = (category || '').toLowerCase();
  return SPEC_FIELDS.filter((f) => f.categories === 'all' || f.categories.includes(c));
}

/** Human-readable value for read-only display, or null if nothing to show. */
export function displaySpecValue(field, raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (field.key === 'formulation' && FORMULATIONS[raw]) return `${raw} – ${FORMULATIONS[raw]}`;
  if (field.type === 'date') {
    try {
      return new Date(raw).toLocaleDateString('en-GB');
    } catch {
      return String(raw);
    }
  }
  if (field.type === 'select') return String(raw).charAt(0).toUpperCase() + String(raw).slice(1);
  return String(raw);
}

/** [{key, label, value}] for every field the product actually has a value for. */
export function specRowsForProduct(product) {
  if (!product) return [];
  return fieldsForCategory(product.productCategory)
    .map((f) => ({ key: f.key, label: fieldLabel(f, product.productCategory), value: displaySpecValue(f, product[f.key]) }))
    .filter((row) => row.value !== null);
}
